/**
 * FERP Portal - Form Renderer
 *
 * Main orchestration component for DSL-driven form rendering.
 * Handles form state, validation, submission, and layout.
 *
 * CONSTRAINTS:
 * - All form state must be centralized
 * - Validation must be real-time
 * - Life-critical forms require confirmation
 * - Audit logging for all submissions
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import {
  Box,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Grid,
  Alert,
  Modal,
  Divider,
  Badge,
  LoadingOverlay,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconAlertCircle,
} from '@tabler/icons-react';
import { FieldRenderer } from './FieldRenderer';
import { WizardStepper, StepNavigation, StepSummary, AccordionSteps } from './StepRenderer';
import { useConditionEvaluator } from './ConditionEvaluator';
import { useDataSourceResolver } from './DataSourceResolver';
import { useTenantId } from '@/auth';
import type {
  FormDefinition,
  FormRendererProps,
  FormState,
  RendererContext,
  FieldDefinition,
  StepDefinition,
  OptionItem,
  Condition,
  DataSource,
  FieldRendererProps,
} from './types';

// ============================================================================
// FORM CONTEXT
// ============================================================================

const FormContext = createContext<RendererContext | null>(null);

/**
 * Hook to access form context
 */
export function useFormContext(): RendererContext {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormRenderer');
  }
  return context;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate a single field
 */
function validateField(field: FieldDefinition, value: unknown): string | undefined {
  const { validation } = field;
  if (!validation) return undefined;

  // Required validation
  if (validation.required) {
    if (value === null || value === undefined || value === '') {
      return validation.errorMessages?.required || `${field.label} is required`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return validation.errorMessages?.required || `${field.label} is required`;
    }
  }

  // Skip other validations if value is empty and not required
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  // String validations
  if (typeof value === 'string') {
    // Min length
    if (validation.minLength && value.length < validation.minLength) {
      return (
        validation.errorMessages?.minLength ||
        `${field.label} must be at least ${validation.minLength} characters`
      );
    }

    // Max length
    if (validation.maxLength && value.length > validation.maxLength) {
      return (
        validation.errorMessages?.maxLength ||
        `${field.label} must be at most ${validation.maxLength} characters`
      );
    }

    // Pattern
    if (validation.pattern) {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(value)) {
        return (
          validation.patternMessage ||
          validation.errorMessages?.pattern ||
          `${field.label} format is invalid`
        );
      }
    }
  }

  // Number validations
  if (typeof value === 'number') {
    // Min
    if (validation.min !== undefined && value < validation.min) {
      return (
        validation.errorMessages?.min ||
        `${field.label} must be at least ${validation.min}`
      );
    }

    // Max
    if (validation.max !== undefined && value > validation.max) {
      return (
        validation.errorMessages?.max ||
        `${field.label} must be at most ${validation.max}`
      );
    }
  }

  return undefined;
}

/**
 * Validate all fields in a form
 */
function validateForm(
  fields: FieldDefinition[],
  values: Record<string, unknown>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const error = validateField(field, values[field.name]);
    if (error) {
      errors[field.name] = error;
    }
  }

  return errors;
}

/**
 * Validate fields in a step
 */
function validateStep(
  step: StepDefinition,
  values: Record<string, unknown>
): Record<string, string> {
  return validateForm(step.fields, values);
}

// ============================================================================
// FORM STATE HOOK
// ============================================================================

interface UseFormStateOptions {
  form: FormDefinition;
  initialValues?: Record<string, unknown>;
  onSubmit?: (values: Record<string, unknown>) => Promise<void>;
}

function useFormState({ form, initialValues, onSubmit }: UseFormStateOptions) {
  // Build initial values from form defaults
  const defaultValues = useMemo(() => {
    const values: Record<string, unknown> = {};

    // Get all fields from form or steps
    const allFields = form.fields || form.steps?.flatMap((s) => s.fields) || [];

    for (const field of allFields) {
      if (field.defaultValue !== undefined) {
        values[field.name] = field.defaultValue;
      } else if (field.value !== undefined) {
        values[field.name] = field.value;
      } else {
        // Set type-appropriate defaults
        switch (field.type) {
          case 'checkbox':
          case 'switch':
            values[field.name] = false;
            break;
          case 'multiselect':
            values[field.name] = [];
            break;
          case 'number':
          case 'currency':
          case 'range':
          case 'rating':
            values[field.name] = null;
            break;
          default:
            values[field.name] = '';
        }
      }
    }

    return { ...values, ...initialValues };
  }, [form, initialValues]);

  // Form state
  const [values, setValues] = useState<Record<string, unknown>>(defaultValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Compute derived state
  const allFields = useMemo(
    () => form.fields || form.steps?.flatMap((s) => s.fields) || [],
    [form]
  );

  const isValid = useMemo(() => {
    const allErrors = validateForm(allFields, values);
    return Object.keys(allErrors).length === 0;
  }, [allFields, values]);

  const isDirty = useMemo(() => {
    return Object.keys(values).some((key) => values[key] !== defaultValues[key]);
  }, [values, defaultValues]);

  // Actions
  const setValue = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear error when value changes
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const setError = useCallback((name: string, error: string) => {
    setErrors((prev) => ({ ...prev, [name]: error }));
  }, []);

  const setFieldTouched = useCallback((name: string, isTouched: boolean) => {
    setTouched((prev) => ({ ...prev, [name]: isTouched }));

    // Validate on blur
    if (isTouched) {
      const field = allFields.find((f) => f.name === name);
      if (field) {
        const error = validateField(field, values[name]);
        if (error) {
          setErrors((prev) => ({ ...prev, [name]: error }));
        } else {
          setErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
          });
        }
      }
    }
  }, [allFields, values]);

  const reset = useCallback(() => {
    setValues(defaultValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
    setSubmitError(null);
    setCurrentStep(0);
  }, [defaultValues]);

  const submit = useCallback(async () => {
    // Validate all fields
    const allErrors = validateForm(allFields, values);

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Touch all fields with errors
      const touchedFields: Record<string, boolean> = {};
      for (const name of Object.keys(allErrors)) {
        touchedFields[name] = true;
      }
      setTouched((prev) => ({ ...prev, ...touchedFields }));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit?.(values);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [allFields, values, onSubmit]);

  const goToStep = useCallback((step: number) => {
    if (step < 0) return;
    if (form.steps && step >= form.steps.length) return;

    // Validate current step before moving forward
    if (form.steps && step > currentStep) {
      const currentStepDef = form.steps[currentStep];
      if (!currentStepDef) return;
      const stepErrors = validateStep(currentStepDef, values);

      if (Object.keys(stepErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...stepErrors }));
        // Touch all fields with errors
        const touchedFields: Record<string, boolean> = {};
        for (const name of Object.keys(stepErrors)) {
          touchedFields[name] = true;
        }
        setTouched((prev) => ({ ...prev, ...touchedFields }));
        return;
      }
    }

    setCurrentStep(step);
  }, [form.steps, currentStep, values]);

  // State object
  const state: FormState = {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    isDirty,
    currentStep,
  };

  return {
    state,
    setValue,
    setError,
    setTouched: setFieldTouched,
    reset,
    submit,
    goToStep,
    submitError,
  };
}

// ============================================================================
// FORM RENDERER
// ============================================================================

/**
 * Main form renderer component
 */
export function FormRenderer({
  form,
  initialValues,
  onSubmit,
  onCancel,
  customRenderers,
}: FormRendererProps) {
  const tenantId = useTenantId();
  const [confirmationOpen, { open: openConfirmation, close: closeConfirmation }] =
    useDisclosure(false);

  // Form state
  const {
    state,
    setValue,
    setError,
    setTouched,
    reset,
    submit,
    goToStep,
    submitError,
  } = useFormState({
    form,
    initialValues,
    onSubmit: async (values) => {
      if (form.submission?.confirmation?.enabled) {
        closeConfirmation();
      }
      await onSubmit?.(values);
    },
  });

  // Condition evaluator
  const evaluateCondition = useConditionEvaluator(state.values);

  // Data source resolver
  const loadDataSource = useDataSourceResolver();

  // Renderer context
  const context: RendererContext = useMemo(
    () => ({
      state,
      setValue,
      setError,
      setTouched,
      submit,
      reset,
      goToStep,
      tenantId,
      loadDataSource,
      evaluateCondition,
      customRenderers,
    }),
    [state, setValue, setError, setTouched, submit, reset, goToStep, tenantId, loadDataSource, evaluateCondition, customRenderers]
  );

  // Handle submit with optional confirmation
  const handleSubmit = useCallback(() => {
    if (form.submission?.confirmation?.enabled) {
      openConfirmation();
    } else {
      submit();
    }
  }, [form.submission?.confirmation?.enabled, openConfirmation, submit]);

  // Compute step states for wizard
  const { completedSteps, errorSteps } = useMemo(() => {
    if (!form.steps) return { completedSteps: new Set<number>(), errorSteps: new Set<number>() };

    const completed = new Set<number>();
    const errors = new Set<number>();

    form.steps.forEach((step, index) => {
      const stepErrors = validateStep(step, state.values);
      const hasErrors = Object.keys(stepErrors).length > 0;

      // A step is completed if it has no errors and all required fields are filled
      const hasRequiredValues = step.fields
        .filter((f) => f.validation?.required)
        .every((f) => {
          const value = state.values[f.name];
          return value !== null && value !== undefined && value !== '';
        });

      if (hasRequiredValues && !hasErrors) {
        completed.add(index);
      }

      // Check if any touched field has errors
      const hasTouchedErrors = step.fields.some(
        (f) => state.touched[f.name] && state.errors[f.name]
      );
      if (hasTouchedErrors) {
        errors.add(index);
      }
    });

    return { completedSteps: completed, errorSteps: errors };
  }, [form.steps, state.values, state.errors, state.touched]);

  // Render based on layout
  const renderFormContent = () => {
    switch (form.layout) {
      case 'wizard':
        return renderWizardLayout();
      case 'grid':
        return renderGridLayout();
      case 'horizontal':
        return renderHorizontalLayout();
      case 'inline':
        return renderInlineLayout();
      case 'vertical':
      default:
        return renderVerticalLayout();
    }
  };

  // Vertical layout (default)
  const renderVerticalLayout = () => {
    const fields = form.fields || [];
    return (
      <Stack gap="md">
        {fields.map((field) => (
          <FieldRenderer
            key={field.name}
            field={field}
            value={state.values[field.name]}
            error={state.touched[field.name] ? state.errors[field.name] : undefined}
            touched={state.touched[field.name]}
            onChange={(value) => setValue(field.name, value)}
            onBlur={() => setTouched(field.name, true)}
            context={context}
          />
        ))}
      </Stack>
    );
  };

  // Horizontal layout
  const renderHorizontalLayout = () => {
    const fields = form.fields || [];
    return (
      <Stack gap="md">
        {fields.map((field) => (
          <Group key={field.name} ta="left">
            <Box style={{ minWidth: 150, flexShrink: 0 }}>
              <Text size="sm" fw={500}>
                {field.label}
                {field.validation?.required && (
                  <span style={{ color: 'var(--mantine-color-red-6)' }}> *</span>
                )}
              </Text>
            </Box>
            <Box style={{ flex: 1 }}>
              <FieldRenderer
                key={field.name}
                field={{ ...field, label: '' }} // Remove label since it's shown separately
                value={state.values[field.name]}
                error={state.touched[field.name] ? state.errors[field.name] : undefined}
                touched={state.touched[field.name]}
                onChange={(value) => setValue(field.name, value)}
                onBlur={() => setTouched(field.name, true)}
                context={context}
              />
            </Box>
          </Group>
        ))}
      </Stack>
    );
  };

  // Grid layout
  const renderGridLayout = () => {
    const fields = form.fields || [];
    return (
      <Grid>
        {fields.map((field) => {
          // Determine column span based on field width
          let span: number;
          switch (field.width) {
            case 'full':
              span = 12;
              break;
            case 'half':
              span = 6;
              break;
            case 'third':
              span = 4;
              break;
            case 'quarter':
              span = 3;
              break;
            default:
              span = 6;
          }

          return (
            <Grid.Col key={field.name} span={span}>
              <FieldRenderer
                field={field}
                value={state.values[field.name]}
                error={state.touched[field.name] ? state.errors[field.name] : undefined}
                touched={state.touched[field.name]}
                onChange={(value) => setValue(field.name, value)}
                onBlur={() => setTouched(field.name, true)}
                context={context}
              />
            </Grid.Col>
          );
        })}
      </Grid>
    );
  };

  // Inline layout
  const renderInlineLayout = () => {
    const fields = form.fields || [];
    return (
      <Group ta="right" gap="md">
        {fields.map((field) => (
          <FieldRenderer
            key={field.name}
            field={field}
            value={state.values[field.name]}
            error={state.touched[field.name] ? state.errors[field.name] : undefined}
            touched={state.touched[field.name]}
            onChange={(value) => setValue(field.name, value)}
            onBlur={() => setTouched(field.name, true)}
            context={context}
          />
        ))}
      </Group>
    );
  };

  // Wizard layout
  const renderWizardLayout = () => {
    if (!form.steps || form.steps.length === 0) {
      console.warn('Wizard layout requires steps');
      return renderVerticalLayout();
    }

    const currentStepDef = form.steps[state.currentStep];
    if (!currentStepDef) {
      console.warn('Current step not found');
      return renderVerticalLayout();
    }

    const isFirstStep = state.currentStep === 0;
    const isLastStep = state.currentStep === form.steps.length - 1;

    // Get current step validation state
    const currentStepErrors = validateStep(currentStepDef, state.values);
    const isStepValid = Object.keys(currentStepErrors).length === 0;

    return (
      <Stack gap="lg">
        <WizardStepper
          steps={form.steps}
          currentStep={state.currentStep}
          completedSteps={completedSteps}
          errorSteps={errorSteps}
          context={context}
          onStepClick={(step) => goToStep(step)}
          allowStepNavigation={true}
        />

        <StepNavigation
          currentStep={state.currentStep}
          totalSteps={form.steps.length}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isStepValid={isStepValid}
          isSubmitting={state.isSubmitting}
          onPrevious={() => goToStep(state.currentStep - 1)}
          onNext={() => goToStep(state.currentStep + 1)}
          onSubmit={handleSubmit}
          submitLabel={form.submitLabel}
        />
      </Stack>
    );
  };

  // Safety level badge
  const renderSafetyBadge = () => {
    if (!form.safetyLevel || form.safetyLevel === 'standard') return null;

    const colors: Record<string, string> = {
      elevated: 'yellow',
      critical: 'orange',
      'life-critical': 'red',
    };

    return (
      <Badge color={colors[form.safetyLevel]} variant="light" leftSection={<IconAlertTriangle size={12} />}>
        {form.safetyLevel.toUpperCase()}
      </Badge>
    );
  };

  return (
    <FormContext.Provider value={context}>
      <Paper p="lg" pos="relative">
        <LoadingOverlay visible={state.isSubmitting} overlayProps={{ blur: 2 }} />

        {/* Form Header */}
        <Group justify="space-between" mb="md">
          <Box>
            <Group gap="sm">
              <Title order={3}>{form.name}</Title>
              {renderSafetyBadge()}
            </Group>
            {form.description && (
              <Text c="dimmed" size="sm" mt={4}>
                {form.description}
              </Text>
            )}
          </Box>
          <Badge variant="light" color="gray">
            v{form.version}
          </Badge>
        </Group>

        <Divider mb="lg" />

        {/* Submit Error */}
        {submitError && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            mb="lg"
            withCloseButton
            onClose={() => setError('_submit', '')}
          >
            {submitError}
          </Alert>
        )}

        {/* Form Content */}
        {renderFormContent()}

        {/* Form Actions (for non-wizard layouts) */}
        {form.layout !== 'wizard' && (
          <>
            <Divider my="lg" />
            <Group justify="flex-end">
              {form.showReset && (
                <Button variant="subtle" onClick={reset} disabled={!state.isDirty || state.isSubmitting}>
                  {form.resetLabel || 'Reset'}
                </Button>
              )}
              {form.showCancel && onCancel && (
                <Button variant="light" onClick={onCancel} disabled={state.isSubmitting}>
                  {form.cancelLabel || 'Cancel'}
                </Button>
              )}
              <Button onClick={handleSubmit} loading={state.isSubmitting} disabled={!state.isValid}>
                {form.submitLabel || 'Submit'}
              </Button>
            </Group>
          </>
        )}

        {/* Confirmation Modal */}
        <Modal
          opened={confirmationOpen}
          onClose={closeConfirmation}
          title={form.submission?.confirmation?.title || 'Confirm Submission'}
          centered
        >
          <Text mb="lg">{form.submission?.confirmation?.message || 'Are you sure you want to submit this form?'}</Text>

          {form.safetyLevel === 'life-critical' && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="lg">
              This is a life-critical operation. Please review all information carefully before confirming.
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="light" onClick={closeConfirmation}>
              {form.submission?.confirmation?.cancelText || 'Cancel'}
            </Button>
            <Button
              color={form.submission?.confirmation?.type === 'danger' ? 'red' : 'blue'}
              onClick={submit}
              loading={state.isSubmitting}
            >
              {form.submission?.confirmation?.confirmText || 'Confirm'}
            </Button>
          </Group>
        </Modal>
      </Paper>
    </FormContext.Provider>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export default FormRenderer;
